import { ReactElement, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { useForm } from '@tanstack/react-form'

import {
  Box,
  Card,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

import axios, { pluckData, pluckError } from 'src/utilities/http-client'
import {
  CancelIcon,
  SubmitIcon,
  UploadFileIcon,
  UploadIcon,
} from 'src/v3/core/icons'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { FileData, handleImageCapture } from 'src/v3/utilities/filesystem'
import {
  fetchCredentials,
  fetchCredentialVerifications,
  fetchUnstartedComplianceCredentialItems,
} from 'src/v3/profile/profile.slice'
import { selectUseCompliance } from 'src/v3/settings/settings.slice'
import { CredentialV2, CREDENTIAL_TYPES } from 'src/v3/core/types'

type CredentialFormProps = {
  credential?: CredentialV2
  initialType?: string
  open: boolean
  setOpen: (status: boolean) => void
  onError: (error: string) => void
}

const other = 'Other'

let normalizeCredentialType = (
  type?: string,
): { type: string; other?: string } => {
  if (!type) return { type: 'unselected' }
  let match = CREDENTIAL_TYPES.find(
    (t) => t !== other && t.toLowerCase() === type.toLowerCase(),
  )
  if (match) return { type: match }
  return { type: other, other: type }
}

export let CredentialForm = ({
  credential,
  initialType,
  open,
  setOpen,
  onError,
}: CredentialFormProps): ReactElement => {
  let dispatch = useAppDispatch()
  let useCompliance = useAppSelector(selectUseCompliance)

  let [image, setImage] = useState<FileData | undefined>(undefined)
  let [saving, setSaving] = useState(false)
  let fileInputRef = useRef<HTMLInputElement>(null)

  let fromCompliance = !!initialType

  let preFill = !credential ? normalizeCredentialType(initialType) : undefined

  let form = useForm({
    defaultValues: {
      number: credential?.credentialIDNumber ?? '',
      type:
        !!credential?.type && !!credential.type.kind
          ? credential.type.kind
          : (preFill?.type ?? 'unselected'),
      issueDate: credential?.issueDate ?? '',
      expiration: credential?.expiration ?? '',
      other:
        credential?.type.kind === other
          ? credential.type.value
          : preFill?.other,
    },
    onSubmit: async ({ value, formApi }) => {
      if (fromCompliance && !image) return
      let request = !credential ? axios.post : axios.put
      setSaving(true)
      request('/api/v2/profile/credential', {
        id: credential?.id,
        credentialIDNumber: value.number,
        type: value.type === other ? { other: value.other } : value.type,
        issueDate: value.issueDate,
        expiration: value.expiration,
      })
        .then(pluckData)
        .then((data) => {
          if (!credential && useCompliance) {
            axios
              .post('/api/profile/compliance/link-credential', { id: data.id })
              .then(() => dispatch(fetchUnstartedComplianceCredentialItems()))
              .catch((err) =>
                onError(
                  pluckError(err, 'Error linking credential to compliance'),
                ),
              )
          }
          if (image) {
            axios
              .put<string>('/api/v2/profile/credential/image', {
                id: credential?.id || data.id,
                imageType:
                  image.extension === 'jpeg' || image.extension === 'jpg'
                    ? 'jpg'
                    : image.extension === 'png'
                      ? 'png'
                      : 'pdf',
              })
              .then(pluckData)
              .then((response) =>
                axios.put(response, image?.file, {
                  headers: {
                    'Cache-Control': 'max-age=2592000',
                    'content-type': image?.type,
                  },
                }),
              )
          }
        })
        .then(() => dispatch(fetchCredentials()))
        .then(() => dispatch(fetchCredentialVerifications()))
        .then(() => setOpen(false))
        .then(() => formApi.reset())
        .catch((err) => {
          onError(pluckError(err, 'Error Updating Credential'))
        })
        .finally(() => setSaving(false))
    },
  })

  let handleOnClose = () => {
    form.reset()
    setImage(undefined)
    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={handleOnClose}>
      <Card
        sx={{
          width: 300,
          px: 2,
          py: 1,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', mb: -2.25 }}>
          {!credential ? 'Add' : 'Edit'} Credential
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogContent
            sx={{
              px: 1,
              pb: 1,
              display: 'flex',
              gap: 1.25,
              flexDirection: 'column',
            }}
          >
            <form.Field
              name="type"
              validators={{
                onChange: ({ value }) =>
                  value === 'unselected'
                    ? 'Credential Type is a required field'
                    : undefined,
              }}
              children={(field) => (
                <FormControl
                  fullWidth
                  error={field.state.meta.errors.length > 0}
                >
                  <InputLabel>Credential Type</InputLabel>
                  <Select
                    sx={{ bgcolor: 'background.paper', mb: -0.5 }}
                    variant="outlined"
                    value={field.state.value}
                    label="Credential Type"
                    name={field.name}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={fromCompliance}
                  >
                    <MenuItem value="unselected">Unselected</MenuItem>
                    {CREDENTIAL_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {field.state.meta.errors.join(', ')}
                  </FormHelperText>
                </FormControl>
              )}
            />
            <form.Subscribe
              selector={(state) => state.values.type}
              children={(type) =>
                type === other ? (
                  <form.Field
                    name="other"
                    validators={{
                      onChange: ({ value }) =>
                        !value ? 'Other is a required field.' : undefined,
                    }}
                    children={(field) => (
                      <TextField
                        sx={{ bgcolor: 'background.paper' }}
                        fullWidth
                        variant="outlined"
                        value={field.state.value}
                        label="Custom Credential Type"
                        name={field.name}
                        disabled={fromCompliance}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        error={field.state.meta.errors.length > 0}
                        helperText={field.state.meta.errors.join(', ')}
                      />
                    )}
                  />
                ) : (
                  <></>
                )
              }
            />
            <form.Field
              name="number"
              validators={{
                onChange: ({ value }) =>
                  !value
                    ? 'Credential ID Number is a required field.'
                    : undefined,
              }}
              children={(field) => (
                <TextField
                  sx={{ bgcolor: 'background.paper' }}
                  fullWidth
                  variant="outlined"
                  value={field.state.value}
                  label="Credential ID Number"
                  name={field.name}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  error={field.state.meta.errors.length > 0}
                  helperText={field.state.meta.errors.join(', ')}
                />
              )}
            />
            <form.Field
              name="issueDate"
              validators={{
                onChangeListenTo: ['expiration'],
                onChange: ({ value, fieldApi }) =>
                  !!value &&
                  !!fieldApi.form.getFieldValue('expiration') &&
                  !dayjs(value).isBefore(
                    fieldApi.form.getFieldValue('expiration'),
                  )
                    ? 'Credential Issue Date must be prior to its Expiration'
                    : undefined,
              }}
              children={(field) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    sx={{ bgcolor: 'background.paper' }}
                    label="Issue Date"
                    name={field.name}
                    value={dayjs(field.state.value)}
                    onChange={(value) =>
                      field.handleChange(
                        !value ? '' : value.format('MM/DD/YYYY'),
                      )
                    }
                    slotProps={{
                      textField: {
                        error: field.state.meta.errors.length > 0,
                        helperText: field.state.meta.errors.join(', '),
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
            <form.Field
              name="expiration"
              validators={{
                onChange: ({ value }) =>
                  !value
                    ? 'Credential Expiration is a required field'
                    : undefined,
              }}
              children={(field) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    sx={{ bgcolor: 'background.paper' }}
                    label="Expiration"
                    name={field.name}
                    value={dayjs(field.state.value)}
                    onChange={(value) =>
                      field.handleChange(
                        !value ? '' : value.format('MM/DD/YYYY'),
                      )
                    }
                    slotProps={{
                      textField: {
                        error: field.state.meta.errors.length > 0,
                        helperText: field.state.meta.errors.join(', '),
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
            <Box>
              <input
                ref={fileInputRef}
                accept="image/*,.pdf"
                style={{ display: 'none' }}
                type="file"
                onChange={(e) => handleImageCapture(e, setImage, onError)}
              />
              <Button
                type="button"
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                {!credential || !credential?.imageName ? 'Select' : 'Overwrite'}{' '}
                File
              </Button>
              {image && (
                <Typography variant="subtitle1">
                  <UploadFileIcon
                    color="primary"
                    sx={{
                      mr: '2px',
                      mb: '2px',
                    }}
                  />
                  {image.name}
                </Typography>
              )}
              {fromCompliance && !image && (
                <Typography color="error" variant="subtitle2">
                  *A file is required to satisfy your manager's requirement.
                </Typography>
              )}
              {credential?.imageName && (
                <Typography color="text.secondary" variant="subtitle2">
                  *The existing credential file will only be overwritten upon
                  submitting this form with a new image uploaded.
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pb: 1.5,
            }}
          >
            <form.Subscribe
              selector={(state) => state.isValid}
              children={(isValid) => (
                <Button
                  color="primary"
                  type="submit"
                  variant="contained"
                  sx={{
                    flex: '1 1 0px',
                  }}
                  startIcon={<SubmitIcon />}
                  disabled={!isValid || (fromCompliance && !image)}
                  loading={saving}
                >
                  Submit
                </Button>
              )}
            />
            <Button
              color="error"
              onClick={handleOnClose}
              variant="contained"
              sx={{
                flex: '1 1 0px',
              }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
      </Card>
    </Dialog>
  )
}
